import {
  createContext,
  useContext,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { OTPInput, OTPInputContext } from 'input-otp'

import { cn } from '@/lib/utils'

// Style variant shared from the root so every slot renders consistently
// without threading a prop through each call site (mirrors TextField's
// `inverse` prop for dark surfaces like the login screens).
const InputOTPInverseContext = createContext(false)

// OTPInput's props are a render-fn/children union; this wrapper is always
// composed with slot children, so narrow to the children arm for clean
// consumer typing.
type InputOTPProps = Omit<
  ComponentProps<typeof OTPInput>,
  'render' | 'children'
> & {
  children: ReactNode
  inverse?: boolean
}

const InputOTP = ({
  className,
  containerClassName,
  inverse = false,
  ...props
}: InputOTPProps) => (
  <InputOTPInverseContext.Provider value={inverse}>
    <OTPInput
      data-slot='input-otp'
      containerClassName={cn(
        'flex items-center gap-2 has-[:disabled]:opacity-50',
        containerClassName
      )}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  </InputOTPInverseContext.Provider>
)

type InputOTPGroupProps = ComponentProps<'div'>

const InputOTPGroup = ({ className, ...props }: InputOTPGroupProps) => (
  <div
    data-slot='input-otp-group'
    className={cn('flex items-center gap-2', className)}
    {...props}
  />
)

type InputOTPSlotProps = ComponentProps<'div'> & {
  index: number
}

const InputOTPSlot = ({ index, className, ...props }: InputOTPSlotProps) => {
  const inputOTPContext = useContext(OTPInputContext)
  const inverse = useContext(InputOTPInverseContext)
  const slot = inputOTPContext?.slots[index]
  const char = slot?.char
  const hasFakeCaret = slot?.hasFakeCaret
  const isActive = slot?.isActive

  return (
    <div
      data-slot='input-otp-slot'
      data-active={isActive}
      className={cn(
        // `border-solid` is required: Tailwind preflight is disabled in this
        // app, so the bare `border` utility has no border-style to inherit.
        'relative flex items-center justify-center border border-solid font-medium transition-shadow duration-sm data-[active=true]:z-10',
        inverse
          ? [
              'h-[48px] w-[34px] rounded-[4px] border-[#d1d2d4] bg-[#1a2937] text-[34px] text-white',
              'data-[active=true]:border-[#79c4d1] data-[active=true]:bg-[#213445] data-[active=true]:shadow-[1px_2px_6px_1px_rgba(126,126,126,0.24)]',
            ]
          : [
              'h-12 w-10 rounded-lg border-input-border bg-white text-2xl text-secondary shadow-input',
              'data-[active=true]:border-input-ring data-[active=true]:shadow-input-focus',
            ],
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
          <div
            className={cn(
              'h-[24px] w-px animate-caret-blink duration-2xl',
              inverse ? 'bg-white' : 'bg-secondary'
            )}
          />
        </div>
      )}
    </div>
  )
}

type InputOTPSeparatorProps = ComponentProps<'div'>

const InputOTPSeparator = ({ className, ...props }: InputOTPSeparatorProps) => (
  <div
    data-slot='input-otp-separator'
    role='separator'
    aria-hidden
    className={cn('text-gray', className)}
    {...props}
  >
    &#8211;
  </div>
)

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
export type { InputOTPProps, InputOTPGroupProps, InputOTPSlotProps }
